import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { StatusLabel } from "@/studio/ui";
import { useStartStage } from "@/studio/ProjectPanels";
import { run } from "@/studio/v1/common";

export const TABS = [
  ["overview", "Overview"], ["consultation", "Consultation"], ["pricing", "Pricing & Budgets"], ["proposal", "Proposal & Agreement"],
  ["payments", "Payments"], ["boards", "Design & Mood Boards"], ["products", "Furniture & Products"], ["ordering", "Ordering & Delivery"], ["documents", "Documents & Activity"],
];

export const useNextAction = (project, onDone, setTab) => {
  const start = useStartStage();
  const [busy, setBusy] = useState(false);
  const na = project.workflow.next_action;
  const go = async () => {
    if (na.kind === "consultation") { start(project.id, na.stage); return; }
    if (na.tab) setTab?.(na.tab);
    if (na.kind !== "api") return;
    setBusy(true);
    await run(adminApi[na.api.method](na.api.path, na.api.body), `${na.label} — done`, onDone);
    setBusy(false);
  };
  return { na, go, busy };
};

export const NextActionButton = ({ project, onDone, setTab, testId = "next-action-button", small }) => {
  const { na, go, busy } = useNextAction(project, onDone, setTab);
  if (na.kind === "none") return <span className="st-tag st-tag--ok" data-testid={testId}>{na.label}</span>;
  return (
    <button className={`st-btn ${small ? "st-btn--sm" : ""}`} disabled={busy} onClick={go} data-testid={testId}>
      {busy ? "Working…" : na.label} <ArrowRight size={14} />
    </button>
  );
};

export const HubHeader = ({ project, onDone, setTab }) => {
  const pr = project.workflow.progress;
  const pct = Math.round((pr.done / pr.total) * 100);
  return (
    <section className="st-hubhead" data-testid="project-hub-header">
      <div className="st-hubhead__row">
        <StatusLabel status={project.status} testId="project-status-label" />
        <span className="st-small st-muted" data-testid="project-progress-text">{pr.done} of {pr.total} steps done</span>
        <span style={{ marginLeft: "auto" }}><NextActionButton project={project} onDone={onDone} setTab={setTab} /></span>
      </div>
      <div className="st-progress" aria-label="Project progress" data-testid="project-progress-bar"><i style={{ width: `${pct}%` }} /></div>
      <ol className="st-steps" data-testid="project-steps">
        {pr.steps.map((s) => <li key={s.label} className={s.done ? "is-done" : ""}>{s.label}</li>)}
      </ol>
      {project.exceptions?.length > 0 && <p className="st-note st-note--warn" data-testid="project-exceptions-banner">Exception{project.exceptions.length > 1 ? "s" : ""} recorded: {project.exceptions.map((e) => e.gate_label).join(", ")}</p>}
    </section>
  );
};

export const HubTabs = ({ tab, setTab }) => (
  <nav className="st-hubtabs" role="tablist" data-testid="project-hub-tabs">
    {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "is-on" : ""} onClick={() => setTab(k)} data-testid={`hub-tab-${k}`}>{l}</button>)}
  </nav>
);
